import { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  AppBar, Box, Button, Chip, IconButton, Menu, MenuItem, Snackbar, Stack, Toolbar,
  Tooltip, Typography,
  useColorScheme,
} from '@mui/material';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';

import { FEATURES } from './config/features';
import { PlanChooserPage } from './pages/PlanChooserPage';
import { CoveragePage } from './pages/CoveragePage';
import { SavedRoutesPage } from './pages/SavedRoutesPage';
import { AdminPage } from './pages/AdminPage';
import { api } from './api/client';
import { useSession } from './components/RequireAuth';
import type { HealthReport } from './api/types';
import { ChangePasswordDialog } from './components/ChangePasswordDialog';
import { Authorized, Permission } from './features/authorization';
import { GuidedTour } from './components/GuidedTour';
import { tourFor } from './copy/tours';

/*
 * Admin is shown when any of its screens is.
 *
 * Derived rather than a flag of its own: the section is only ever a container
 * for the modules inside it, so an Admin entry leading to a page with nothing
 * on it would be worse than no entry at all. Places, Themes and Route styles
 * are not gated, so today this is always true — it stops being true the
 * moment they are, and nothing else has to change.
 */
const SHOW_ADMIN = FEATURES.cityImport
  || FEATURES.userManagement
  || FEATURES.categoryConfig
  || FEATURES.plannerSettings
  || FEATURES.planningAgent
  // Places, Themes and Route styles were not asked to be hidden.
  || true;

const NAV = [
  /*
   * One entry, not two. A day out and a week away are the same act of
   * planning at different lengths, and putting them side by side in the menu
   * made a traveller choose before anything had told them what the difference
   * was. The choice is asked on the page instead.
   */
  { path: '/plan', label: 'Plan' },
  /*
   * The catalogue, read on its own.
   *
   * The planner opens the same browser in a window while a round is being
   * built; this is for the question asked before there is a round — what
   * has the team covered, and where is there still new ground. Not gated:
   * it shows what the coverage panel already shows, on work the account
   * can see anyway.
   */
  { path: '/coverage', label: 'Coverage' },
  /*
   * Saved trips and Admin are dropped from the menu in a production build —
   * see src/config/features.ts. Dropped from the LIST, so the menu closes up
   * rather than leaving a gap, and every consumer of NAV agrees on what
   * exists without each of them repeating the test.
   */
  ...(FEATURES.savedTrips ? [{ path: '/saved', label: 'Saved' }] : []),
  ...(SHOW_ADMIN ? [{ path: '/admin', label: 'Admin' }] : []),
];

/**
 * Reference for the planning endpoint — payloads, response shape and the
 * stages it runs through.
 *
 * A static file in `web/public/docs/`, so it ships and versions with the code
 * it documents and needs nothing beyond this app to serve it. Outside the
 * router on purpose: it is a plain document, not a screen, and opens in its
 * own tab so a half-filled plan is never lost to read it.
 */
const DOCS_URL = '/docs/plan-api.html';

export default function App() {
  const location = useLocation();
  const { mode, setMode } = useColorScheme();
  const { session, signOut } = useSession();
  const [health, setHealth] = useState<HealthReport | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [touring, setTouring] = useState(false);
  // Only screens that have a tour written for them offer the button.
  const tour = tourFor(location.pathname);

  // A single poll on load is enough to tell the user an engine is down before
  // they fill in a form and hit an opaque failure.
  useEffect(() => {
    api.health().then(setHealth).catch(() => setHealth(null));
    // Admin is several addresses now, one per module, so this asks whether we
    // are anywhere under it rather than at it exactly — an equality check here
    // stopped firing the moment the page grew its own nav.
  }, [location.pathname.startsWith('/admin')]);

  const dark = mode === 'dark';
  /*
   * Four links, a wordmark, a status chip and a theme toggle do not fit a
   * phone. Below the breakpoint they move into a menu behind one button.
   */
  const [navAnchor, setNavAnchor] = useState<HTMLElement | null>(null);
  const engineDown =
    health && !health.ok
      ? Object.entries(health.engines)
          .filter(([, value]) => !Array.isArray(value) && !value.ok)
          .map(([key]) => key)
      : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar variant="dense" sx={{ gap: 2 }}>
          <Tooltip title="Open the menu">
            <IconButton
              size="small"
              edge="start"
              onClick={(event) => setNavAnchor(event.currentTarget)}
              aria-label="Open the menu"
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <ExploreOutlinedIcon color="primary" />
            {/* The wordmark is the first thing to go; the compass still says
                which app this is. */}
            <Typography
              variant="h3"
              component="span"
              sx={{ display: { xs: 'none', sm: 'inline' } }}
            >
              TravPlaces
            </Typography>
          </Stack>

          {/*
            Two levels of navigation, told apart by weight rather than repeated.

            This and the side nav inside Admin both marked their current entry
            with an accent-coloured pill of about the same size, so the section
            of the app you were in and the page you were on inside it carried
            equal weight — and neither looked like it contained the other.

            The accent FILL belongs here and only here: it answers "where am I
            in the app". The nav inside a section answers a smaller question
            and is drawn quietly, in no colour of its own.
          */}
          <Stack data-tour="nav" direction="row" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
            {NAV.map((item) => {
              const here = location.pathname.startsWith(item.path);

              return (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  size="small"
                  disableElevation
                  color={here ? 'primary' : 'inherit'}
                  variant={here ? 'contained' : 'text'}
                  sx={{ fontWeight: here ? 600 : 500 }}
                >
                  {item.label}
                </Button>
              );
            })}

            {FEATURES.apiDocs && (
            <Tooltip title="How the planning API works">
              <Button
                component="a"
                href={DOCS_URL}
                target="_blank"
                rel="noreferrer"
                size="small"
                color="inherit"
                endIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />}
              >
                API docs
              </Button>
            </Tooltip>
            )}
          </Stack>

          <Box sx={{ flex: 1 }} />

          {engineDown.length > 0 && (
            <Tooltip title={`Not responding: ${engineDown.join(', ')}. Check docker compose ps.`}>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={engineDown.length}
                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              />
            </Tooltip>
          )}
          {engineDown.length > 0 && (
            <Tooltip title={`Not responding: ${engineDown.join(', ')}. Check docker compose ps.`}>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`${engineDown.length} engine down`}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              />
            </Tooltip>
          )}

          <Menu
            anchorEl={navAnchor}
            open={Boolean(navAnchor)}
            onClose={() => setNavAnchor(null)}
            slotProps={{ list: { dense: true } }}
          >
            {NAV.map((item) => (
              <MenuItem
                key={item.path}
                component={RouterLink}
                to={item.path}
                selected={location.pathname.startsWith(item.path)}
                onClick={() => setNavAnchor(null)}
              >
                {item.label}
              </MenuItem>
            ))}
            {FEATURES.apiDocs && (
              <MenuItem
                component="a"
                href={DOCS_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => setNavAnchor(null)}
              >
                API docs
                <OpenInNewOutlinedIcon sx={{ fontSize: 14, ml: 1 }} />
              </MenuItem>
            )}
          </Menu>

          {tour && (
            <Tooltip title="Show me around this screen">
              <IconButton
                data-tour="help"
                size="small"
                onClick={() => setTouring(true)}
                aria-label="Show me around this screen"
              >
                <HelpOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={dark ? 'Switch to light' : 'Switch to dark'}>
            <IconButton size="small" onClick={() => setMode(dark ? 'light' : 'dark')}>
              {dark ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/*
            Every role holds the own-password permission, so this is here for
            everyone — but it is still gated rather than assumed, because a
            role could be created without it and an operator should not be
            shown a button that returns permission denied.
          */}
          {/*
            Both of these are about an account, and with the login off there
            is not one: signing out would return to the app it just left, and
            there is no password anywhere to change. See SessionState.
          */}
          {!session.authDisabled && (
            <>
              <Authorized permission={Permission.PROFILE_PASSWORD}>
                <Tooltip title="Change your password">
                  <IconButton size="small" onClick={() => setChangingPassword(true)}>
                    <KeyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Authorized>

              <Tooltip title={session.username ? `Sign out (${session.username})` : 'Sign out'}>
                <IconButton size="small" onClick={signOut}>
                  <LogoutOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Routes>
          <Route path="/" element={<Navigate to="/plan" replace />} />
          <Route path="/plan" element={<PlanChooserPage />} />
          <Route path="/plan/:kind" element={<PlanChooserPage />} />
          <Route path="/coverage" element={<CoveragePage />} />
          {/* The old address, kept so anything pointing at it still lands. */}
          <Route path="/trip" element={<Navigate to="/plan/trip" replace />} />
          {/*
            Guarded as well as hidden. A menu entry that is not drawn still
            leaves the address reachable by typing it or by an old bookmark,
            and landing on a screen the build is meant not to offer is worse
            than a redirect nobody notices.
          */}
          <Route
            path="/saved"
            element={FEATURES.savedTrips ? <SavedRoutesPage /> : <Navigate to="/plan" replace />}
          />
          <Route
            path="/admin/*"
            element={SHOW_ADMIN ? <AdminPage /> : <Navigate to="/plan" replace />}
          />
        </Routes>
      </Box>

      {/*
        Mounted at the shell rather than inside a page, because changing your
        own password is not part of any one screen — it belongs to the session,
        which is what the header is.
      */}
      <ChangePasswordDialog
        open={changingPassword}
        onClose={() => setChangingPassword(false)}
        onDone={setToast}
      />

      <GuidedTour open={touring && Boolean(tour)} steps={tour ?? []} onClose={() => setTouring(false)} />

      <Snackbar
        open={toast !== null}
        message={toast ?? ''}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
      />
    </Box>
  );
}
